import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UDEU_TRANSFER_PHYSICAL_SUPPORT } from '../../../../model/contants/deusto';
import { KeyValue } from '@angular/common';
import { DateType } from 'projects/alden-extension/src/lib/models/date-types';
import { UdeuDocument } from '../../../../model/classes/udeu-transfer';
import { UdeuService } from '../../../../services/udeu.service';
import { TransferActionTypes } from 'projects/deusto-extension/src/lib/store/actions/transfer-actions';

@Component({
  selector: 'alden-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss']
})
export class DocumentsComponent implements OnInit, OnChanges { 
  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() udeuDocuments: UdeuDocument[];
  @Input() support: KeyValue<string, string>;
  @Input() canEditExtraProps: boolean;
  @Input() actionType: TransferActionTypes;

  DateType: typeof DateType = DateType;
  TransferActionTypes: typeof TransferActionTypes = TransferActionTypes;
  
  private _formBuilder = inject(FormBuilder);
  formGroup: FormGroup<any>;

  loading: boolean;
  disabled: boolean;

  constructor(public udeuService: UdeuService) {}

  ngOnInit(): void {
    this.loading = true;
    this.disabled = this.group.disabled;

    if (this.udeuDocuments) {
      this.udeuDocuments.sort((a,b) =>{
        if(a.signatura && b.signatura)
          return a.signatura.localeCompare(b.signatura);
        else if(a.directory && b.directory)
          return a.directory.localeCompare(b.directory);
        return a.order?.localeCompare(b.order);
      }).forEach((udeuDocument) => {
        const form = this.createForm(udeuDocument);
        this.disabled ? form.disable() : null;
        if(this.canEditExtraProps) {
          form.get('code').enable();
          form.get('signatura').enable();

          form.get('code').addValidators(Validators.required);
          form.get('signatura').addValidators(Validators.required);
        }
        this.formArray.push(form);
      });
    }

    this.loading = false;
  }

  createForm(document?: UdeuDocument): FormGroup<any> {
    return this._formBuilder.group({
      order: [document?.order, [Validators.pattern('^[0-9]{3}-[0-9]{2}$')]],
      directory: [document?.directory],
      title: [document?.title, [Validators.required]],
      content: [document?.content, [Validators.required]],
      date: [document?.date, [Validators.required]],
      dateTo:[document?.dateTo,[Validators.required]],
      code: [document?.code],
      signatura: [document?.signatura],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loading = true;
    this.formGroup = this.createForm();

    const supportChange = changes["support"];
    if(supportChange !== undefined) {
      this.support = supportChange.currentValue;
      this.setOrderAndDirectoryValidators(this.formGroup);
      this.setOrderAndDirectory();
    }

    this.loading = false;
  }

  setOrderAndDirectory() { 
    this.formArray.controls.forEach((formGroup, _index) => {
      if (formGroup instanceof FormGroup) {
        if(this.support?.key === UDEU_TRANSFER_PHYSICAL_SUPPORT) { 
          const control = formGroup.get('directory');
          const value = control.value;
          formGroup.get('order').setValue(value);

          control.reset();
          this.setOrderAndDirectoryValidators(formGroup);
        } else {
          const control = formGroup.get('order');
          const value = control.value;
          formGroup.get('directory').setValue(value);

          control.reset();
          this.setOrderAndDirectoryValidators(formGroup);
        }
      }
    });
  }

  setOrderAndDirectoryValidators(form: FormGroup<any>) {
    if(this.support?.key === UDEU_TRANSFER_PHYSICAL_SUPPORT) {
      form.get('order').addValidators(Validators.required);
      form.get('directory').removeValidators(Validators.required);
    } else {
      form.get('directory').addValidators(Validators.required);
      form.get('order').removeValidators(Validators.required);
    }

    form.get('order').updateValueAndValidity();
    form.get('directory').updateValueAndValidity();
  }

  addDocument() {
    const document = new UdeuDocument();
    document.order = this.formGroup.get('order').value;
    document.directory = this.formGroup.get('directory').value;
    document.title = this.formGroup.get('title').value;
    document.content = this.formGroup.get('content').value;
    document.date = this.formGroup.get('date').value;
    document.dateTo = this.formGroup.get('dateTo').value;
    document.code = this.formGroup.get('code').value;
    document.signatura = this.formGroup.get('signatura').value;

    const form = this.createForm(document);
    this.setOrderAndDirectoryValidators(form);
    this.formArray.push(form);
    this.resetForm();
  }

  resetForm() {
    this.formGroup.reset();
    this.formGroup.setErrors(null);
    this.formGroup.markAsUntouched();
  }

  delete(index: number) {
    const formArray = this.formArray;
    formArray.removeAt(index);
  }

  get formArray(): FormArray {
    return this.group.get(this.controlName) as FormArray;
  }

  formArrayGroup (index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  isUrl(value:string){
    if(!value)
      return false;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }

}
